import { Box, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { FC } from 'react';
import { useDarkMode, useMediaUp } from '../../../hooks';
import { ArrowIcon, GpmLogoIcon } from '../../../svgs/gpm';
import VideoBLock from './VideoBLock';

interface GpmBlockWithImageProps {
  title: string;
  subTitle?: string;
  description?: string;
  hasBgImage?: boolean;
  hasButton?: boolean;
  hasSvg?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    position: 'relative',
    display: 'flex',
    padding: theme.spacing(3, 0, 0),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(15, 0),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(13.5, 0),
    },
  },
  title: {
    textAlign: 'center',
    color: '#0061E5',
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '24px',
    [theme.breakpoints.up('sm')]: {
      fontSize: 45,
      lineHeight: '45px',
      textAlign: 'left',
    },
  },
  subtitle: {
    textAlign: 'center',
    color: (isDarkMode) => (isDarkMode ? theme.palette.common.white : theme.palette.secondary.dark),
    fontSize: 15,
    fontWeight: 500,
    maxWidth: 390,
    lineHeight: '18px',
    fontFamily: 'Helvetica Neue, sans-serif',
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
      lineHeight: '25px',
      textAlign: 'left',
    },
  },
  subtitle2: {
    textAlign: 'center',
    fontFamily: 'Helvetica Neue, sans-serif',
    color: (isDarkMode) => (isDarkMode ? theme.palette.common.white : theme.palette.secondary.dark),
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    maxWidth: 1227,
    [theme.breakpoints.up('sm')]: {
      fontSize: 24,
      lineHeight: '36px',
      textAlign: 'left',
    },
  },
  description: {
    textAlign: 'center',
    color: (isDarkMode) => (isDarkMode ? theme.palette.common.white : theme.palette.secondary.dark),
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: 400,
    marginTop: theme.spacing(2),
    maxWidth: 1062,
    [theme.breakpoints.up('sm')]: {
      fontSize: 16,
      lineHeight: '24px',
      textAlign: 'left',
    },
  },
  link: {
    boxShadow: 'none',
    borderRadius: '0 10px',
    padding: '14px 35px',
    textTransform: 'none',
    fontSize: 15,
    lineHeight: 1,
    fontWeight: 500,
    color: '#0061E5',
    border: '2px solid #0061E5',
    backgroundColor: 'transparent',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0061E5',
      color: '#ffffff',
    },
  },
}));

const GpmBlockWithImage: FC<GpmBlockWithImageProps> = (props) => {
  const { title, subTitle, description, hasBgImage = false, hasSvg = false, hasButton = false } = props;
  const { isDarkMode } = useDarkMode();
  const classes = useStyles(isDarkMode);
  const isSmallUp = useMediaUp('sm');
  const isMediumUp = useMediaUp('md');

  return (
    <Box>
      <Container maxWidth={false} fixed>
        <Grid className={classes.content} alignItems={'center'} justifyContent="space-between" container>
          <Grid md={5} xs={12} item>
            <div>
              {hasSvg && (
                <Box
                  display={'flex'}
                  justifyContent={isSmallUp ? 'left' : 'center'}
                  alignItems={isSmallUp ? 'left' : 'center'}
                  mb={isSmallUp ? 6 : 4}
                >
                  <GpmLogoIcon />
                </Box>
              )}

              <Box
                display={'flex'}
                justifyContent={isSmallUp ? 'left' : 'center'}
                alignItems={isSmallUp ? 'left' : 'center'}
                mb={isSmallUp ? 6 : 4}
              >
                <Typography className={classes.title}>{title}</Typography>
              </Box>
            </div>
            {subTitle && (
              <Box display={'flex'} justifyContent={isSmallUp ? 'left' : 'center'}>
                <Typography className={hasBgImage ? classes.subtitle2 : classes.subtitle}>{subTitle}</Typography>
              </Box>
            )}
            {description && (
              <div>
                <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                  <Typography className={classes.description}>{description}</Typography>
                </Box>
              </div>
            )}
            {hasButton && (
              <Box display={isSmallUp ? 'flex' : 'none'} mt={isSmallUp ? 8 : 5} mb={isSmallUp ? 5 : 2}>
                <a className={classes.link} href={'https://gpmgis.com/'} target="_blank" rel="noopener noreferrer">
                  {'Visit website'}
                </a>
              </Box>
            )}
          </Grid>
          <Grid item md={7} xs={12}>
            <Box mt={10} />
            <VideoBLock />
          </Grid>
          {hasSvg && isMediumUp && (
            <Box position={'absolute'} left={-300} bottom={-40}>
              <ArrowIcon />
            </Box>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default GpmBlockWithImage;
